import { ConfigHelper } from './../../../../services/config.helper';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs/Rx';

import { MyModal } from './../../../common/my-modal/my-modal.component';
import * as Enum from '../../../../dtos/enums';
import { TestNavigationService } from '../../../../services/test-navigation.service';
import { TestTakerService } from '../../../../services/test-taker.service';
import { TestContentService } from '../../../../services/test-content.service';
import { TimerService } from '../../../../services/timer.service';

@Component({
    selector: 'timer',
    template: require('./timer.component.html')
})

export class TimerComponent implements OnInit, OnDestroy {
    @Input() assessmentItem$: Observable<PgRendererDto.AssessmentItem>;
    @Input() assessmentTest: PgRendererDto.AssessmentTest;

    previousAssessmentItem: PgRendererDto.AssessmentItem;
    previousTestletId: number;
    displayMode: string;
    visible: boolean;
    timerIntervalHandle: number;
    timerDisplayText: string;
    runningLowOnTime: boolean;
    speakingPreparationTime: number;
    speakingRecordingTime: number;

    private timerSubscription: Subscription;
    private assessmentItemSubscription: Subscription;
    private previousCanGoNext: boolean;

    constructor(
        private timerService: TimerService,
        private testContentService: TestContentService,
        private testNavigationService: TestNavigationService,
        private testTakerService: TestTakerService,
        private myModal: MyModal,
        private changeDetectorRef: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        this.assessmentItemSubscription = this.assessmentItem$.subscribe(assessmentItem => {
            const testlet = this.testContentService.activeTestlet;
            if (testlet && assessmentItem) {
                const referencedInteractions = this.testContentService.getReferencedInteractions(assessmentItem);
                const firstSpeakingInteraction = referencedInteractions.find(interaction => interaction && interaction.SpeakingInteraction != null);
                const { preparationTime, recordingTime } = this.getSpeakingTimings(firstSpeakingInteraction);

                const timerVisibility = true;

                const differentTestlet = this.previousTestletId !== this.testContentService.activeTestletId;
                const differentAssessmentItemGroup = this.previousAssessmentItem && this.previousAssessmentItem.GroupNumber != assessmentItem.GroupNumber;
                const startTimer = differentTestlet || differentAssessmentItemGroup;
                if (startTimer) {
                    let multiplier = 1;
                    const html = assessmentItem.ItemBody.ItemBodyPanels.map(p => p.Html).join(" ");
                    const autoSkipTimerValue = this.parseAutoSkipTimerValue(html);
                    const listeningTimes = this.parseListeningTimeTimerValue(html);
                    const timeLimits = Object.assign({}, testlet.TimeLimits);
                    if ( listeningTimes ) {
                            timeLimits.MinTime = timeLimits.MaxTime - listeningTimes.reviewTime;
                    }
                    if (ConfigHelper.isTdpMode()) {
                        const { originalMaxTime, multipliers } = this.getAdditionalTime(timeLimits, assessmentItem);
                        this.applyAdditionalTime(timeLimits, multipliers, originalMaxTime, listeningTimes);
                        if (multipliers.length > 0) {
                            multiplier = multipliers[0];
                        }
                    }

                    // only show the time's up dialog box when the testlet's time limit is 60 seconds or more
                    let showTimesUpWarning = timeLimits.MaxTime > 60 && !this.testTakerService.testNavigation.isEndOfTest;
                    if (autoSkipTimerValue) {
                        timeLimits.MinTime = null;
                        timeLimits.MaxTime = autoSkipTimerValue;
                        showTimesUpWarning = false;
                    }
                    // find timeElapsed in this Testlet
                    let timeAlreadyUsedInTestlet = 0;
                    if (ConfigHelper.isTdpMode(true) && this.testContentService.activeTestletId && this.testContentService.useTestletTimeAdjustment()) {
                        timeAlreadyUsedInTestlet = this.timerService.getTimeUsedOnTestlet(this.testContentService.activeTestletId);
                    }
                    this.timerService.startTimer(timeLimits, preparationTime, recordingTime, timerVisibility, () => this.testNavigationService.goNext(false), showTimesUpWarning, multiplier, timeAlreadyUsedInTestlet);
                    this.previousTestletId = this.testContentService.activeTestletId;
                }
                this.previousAssessmentItem = assessmentItem;
            }
        });

        this.timerSubscription = this.timerService.timer$.subscribe(timer => {
            this.clearTimerInterval();
            this.visible = false;
            this.previousCanGoNext = undefined;
            this.runningLowOnTime = false;
            this.processTimer();
            this.changeDetectorRef.detectChanges();
            if (timer.timeLimits && (timer.timeLimits.MinTime || timer.timeLimits.MaxTime)) {
                this.timerIntervalHandle = window.setInterval(() => {
                    this.processTimer();
                }, 1000);
            }

        });
    }

    ngOnDestroy() {
        this.clearTimerInterval();
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }

        if (this.assessmentItemSubscription) {
            this.assessmentItemSubscription.unsubscribe();
        }
    }

    private processTimer() {
        const timer = this.timerService.timer;
        if (timer) {
            if (timer.speakingPreparationTime && timer.speakingRecordingTime) {
                this.displayMode = "speaking";
                this.visible = this.timerService.isTimerVisible;
                this.speakingPreparationTime = timer.speakingPreparationTime;
                this.speakingRecordingTime = timer.speakingRecordingTime;
            } else if (timer.timeLimits) {
                if (ConfigHelper.isTdpMode(true) && this.testContentService.activeTestletId) {
                    this.timerService.saveTimeUsedOnTestlet(this.testContentService.activeTestletId);
                }
                this.displayMode = "standard";
                this.visible = this.timerService.isTimerVisible && timer.timeLimits.MaxTime && timer.timeLimits.MaxTime > 0;
                let displayString = null;
                if (timer.pausedAt) {
                    displayString = "PAUSED";
                } else {
                    let goNextCallbackSet = false;
                    if (timer.timeLimits.MaxTime) {
                        const timeLeft = this.timerService.getTimeLeft();
                        this.runningLowOnTime = timeLeft < 180;
                        displayString = this.timerService.getDisplayString(timeLeft);
                        if (timeLeft === 0 && this.timerIntervalHandle) {
                            this.clearTimerInterval();
                            goNextCallbackSet = true;
                            const timesUpCallback = timer.timesUpCallback ? timer.timesUpCallback : () => {
                                this.testNavigationService.goNext(false);
                            };
                            if (timer.showTimesUpWarning) {
                                this.myModal.showTimesUpDlg(() => this.handleTimeout(timesUpCallback));
                            } else {
                                this.handleTimeout(timesUpCallback);
                            }
                        }
                    }
                    if (!goNextCallbackSet) {
                        this.updateCanGoNext();
                    }
                }
                this.timerDisplayText = displayString;
            }
        }
    }

    private handleTimeout(timesUpCallback: Function): void {
        const currentTestlet = this.testContentService.activeTestlet;
        if (currentTestlet) {
            const assessmentItems = this.testContentService.getReferencedInteractionsByTestlet(currentTestlet);
            if (assessmentItems.length > 0) {
                this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.TestletTimeout, null, null, this.testContentService.activeTestletId);
            }
        }

        timesUpCallback();
    }

    private updateCanGoNext() {
        const canGoNext = this.timerService.canGoNext();
        //only update the store if the value has changed
        if (canGoNext != this.previousCanGoNext) {
            this.previousCanGoNext = canGoNext;
            this.testNavigationService.setCanGoNext(this.timerService.canGoNext());
        }
    }

    private clearTimerInterval() {
        if (this.timerIntervalHandle) {
            window.clearInterval(this.timerIntervalHandle);
            this.timerIntervalHandle = null;
        }
    }

    private parseAutoSkipTimerValue(html: string): number {
        const regex = /<auto-skip-timer>(\d+)<\/auto-skip-timer>/igm;
        let matchedArray: RegExpExecArray;
        if ((matchedArray = regex.exec(html)) != null) {
            return parseInt(matchedArray[1]);
        }
        return null;
    }

    private parseListeningTimeTimerValue(html: string) {
        const attributeRegex = /(<auto-play-resource .*<\/auto-play-resource>)/igm;
        const tagsRegex = /(time|reviewTime)="(\d+)"/igm;

        let matchedAttributeArray: RegExpExecArray;
        let matchedTagsArray: string[];

        let times = {
            time: 0,
            reviewTime: 0
        };

        const existAttributeMatchArray = (matchedAttributeArray = attributeRegex.exec(html)) != null;

        if (existAttributeMatchArray) {
            const attributeMatch = matchedAttributeArray[1].toString();
            const attributeFound = existAttributeMatchArray && attributeMatch;
            const existTagsMatchArray = (matchedTagsArray = this.captureAllTagMatches(attributeMatch, tagsRegex)) != null;

            if (attributeFound && existTagsMatchArray) {
                times = this.resolveProperListeningTimeValues(matchedTagsArray);
                return times;
            }
        }

        return null;
    }

    captureAllTagMatches(matchedAttribute: string, tagsRegex: RegExp) {
        const matchedTags: string[] = [];
        let match: RegExpExecArray;

        while (match = tagsRegex.exec(matchedAttribute)) {
            matchedTags.push(match[1]);
            matchedTags.push(match[2]);
        }

        return matchedTags;
    }

    private getMatchedRegexValue(matchedTagsArray: string[], name: string): number {
        const index = matchedTagsArray.indexOf(name);
        if (index >= 0) {
            return parseInt(matchedTagsArray[index + 1]);
        }
        return null;
    }

    private resolveProperListeningTimeValues(matchedTagsArray: string[]) {
        const time: number = this.getMatchedRegexValue(matchedTagsArray, "time");
        const reviewTime: number = this.getMatchedRegexValue(matchedTagsArray, "reviewTime");

        return {time, reviewTime};
    }

    stopTimer() {
        this.clearTimerInterval();
    }

    private getSpeakingTimings(interaction: PgRendererDto.InteractionDefinition) {

        let preparationTime = interaction ? interaction.SpeakingInteraction.PreparationTime : null;
        let recordingTime = interaction ? interaction.SpeakingInteraction.RecordingTime : null;
        if (preparationTime && recordingTime) {
            if (this.assessmentTest) {
                const timeSpecification = this.assessmentTest.TimingSpecification;
                if (timeSpecification && Object.keys(timeSpecification).length > 0) {
                    const speakingTimeMultiplier = this.findSpeakingSpecification(timeSpecification);
                    if (speakingTimeMultiplier) {
                        if (interaction.SpeakingInteraction.ExtendPrepTime) {
                            preparationTime = preparationTime * speakingTimeMultiplier;
                        }

                        if (interaction.SpeakingInteraction.ExtendSpeakingTime) {
                            recordingTime = recordingTime * speakingTimeMultiplier;
                        }
                    }
                }
            }
        }
        return { preparationTime, recordingTime };
    }

    private findSpeakingSpecification(timeSpecification: PgRendererDto.KeyValuePair<number, number>[]): number {
        const timeSpecificationComponents = Object.keys(timeSpecification).map(value => parseInt(value));
        const speakingTimeSpecification = timeSpecificationComponents.find(x => x == Enum.ComponentTypeEnum.Speaking);

        if (speakingTimeSpecification) {
            const multiplier =  parseFloat(timeSpecification[speakingTimeSpecification.toString()]);
            return  multiplier;
        }

        return 1.0;
    }

    private getAdditionalTime(timeLimits: PgRendererDto.TimeLimits, assessmentItem: PgRendererDto.AssessmentItem) {
        let multipliers: number[] = [];
        const originalMaxTime = timeLimits.MaxTime;

        if (this.assessmentTest) {
            const timeSpecification = this.assessmentTest.TimingSpecification;
            if (timeSpecification != null && Object.keys(timeSpecification).length > 0) {
                const timeSpecificationComponents = Object.keys(timeSpecification).map(value => parseInt(value) );
    
                const interactionDefs = this.testContentService.getReferencedInteractions(assessmentItem);
                const interactionDefComponents = interactionDefs.map(value => value.ComponentTypeId);
    
                const intersectingComponents = timeSpecificationComponents.filter((value) => {
                    return interactionDefComponents.indexOf(value) !== -1;
                });
    
                const originalMaxTime = timeLimits.MaxTime;
                for (let i = 0; i < intersectingComponents.length; i++) {
                    const multiplier =  parseFloat(timeSpecification[intersectingComponents[i].toString()]);
                    multipliers.push(multiplier);
                }
            }
        }
       
        return { originalMaxTime, multipliers };
        
    }

    private applyAdditionalTime(timeLimits: PgRendererDto.TimeLimits, multipliers: number[], originalMaxTime: number, listeningTimes?: any) {
        if (multipliers) {
            for (let i = 0; i < multipliers.length; i++) {
                if (listeningTimes) {
                    const prepPlusPostTime = listeningTimes.time + listeningTimes.reviewTime;
                    const listeningIncrement = prepPlusPostTime * (multipliers[i] - 1.0);
                    timeLimits.MaxTime = originalMaxTime + listeningIncrement;
                } else {
                    timeLimits.MaxTime = originalMaxTime * multipliers[i];
                }
            }
        }
    }

}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/components/pg-renderer/top-nav/timer/timer.component.ts